import { type LanguageDetectorAsyncModule } from 'i18next';
import Backend, { type HttpBackendOptions } from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import {
  globalCountryId,
  rtlLanguages,
  useLocalizationStore,
} from '$/components/core/Localization/hooks/useLocalizationStore';
import { i18n, t } from '$/components/core/Localization/i18nInstance';
import {
  localizationStorageKey,
  persistLocalization,
  type I18nCache,
} from '$/components/core/Localization/localizationPersistence';
import { getMainLanguage } from '$/components/core/Localization/supportedLocalization';
import { envVars } from '$/envVars';
import { getGeolocation } from '$/services/usecases/azure';
import { getAllCountries } from '$/services/usecases/internationalization/countries';
import { updateCountry } from '$/services/usecases/profile';
import { deepMerge } from '$/utils/objectUtils';

const languageDetectorConfig = {
  localStorageKey: localizationStorageKey,
};

export const detectLanguageAndCountry = async (): Promise<I18nCache> => {
  const { localStorageKey } = languageDetectorConfig;
  let detected: I18nCache = { language: 'en', country: globalCountryId };

  const { region, language } = new Intl.Locale(navigator.language);
  detected.language = language;
  detected.country = region?.toLowerCase() ?? globalCountryId;

  const user = useAuthenticationStore.getState().user;
  const localStorageItem = localStorage.getItem(localStorageKey);
  const existingCountries = await getAllCountries();

  if (localStorageItem) {
    detected = JSON.parse(localStorageItem) as I18nCache;
    const country = existingCountries.response.payload.find(
      (country) => country.id === detected.country,
    );
    if (country != null && !country.languages.includes(detected.language)) {
      detected.language = getMainLanguage(country);
    }
  } else if (user?.country != null) {
    detected.country = user.country;
    const country = existingCountries.response.payload.find(
      (country) => country.id === detected.country,
    );
    detected.language = getMainLanguage(country);
  } else {
    try {
      const ip = await getGeolocation();
      const ipIsoCode = ip.response.payload.countryRegion?.isoCode;

      if (ipIsoCode) {
        if (existingCountries.isSuccessful) {
          detected.country = existingCountries.response.payload.some(
            (country) => country.id === ipIsoCode.toLowerCase(),
          )
            ? ipIsoCode.toLowerCase()
            : globalCountryId;
        } else {
          detected.country = globalCountryId;
        }
        const country = existingCountries.response.payload.find(
          (country) => country.id === detected.country,
        );
        detected.language = getMainLanguage(country);
      } else {
        detected = { language: 'en', country: 'de' };
      }
    } catch (error) {
      console.error(error);
    }
  }

  if (user && (user.country !== detected.country || !detected.country)) {
    void updateCountry(detected.country).catch(() => {});
  }

  const search = new URLSearchParams(window.location.search);
  if (search.has('brand')) {
    detected.country = search.get('brand')!;
  }

  const overrideCountryId = search.get('country')?.toLowerCase();
  const overrideLanguageId = search.get('language')?.toLowerCase();

  if (overrideCountryId) {
    detected.country = overrideCountryId;
  }

  if (overrideLanguageId) {
    detected.language = overrideLanguageId;
  }

  return detected;
};

const languageDetector: LanguageDetectorAsyncModule = {
  type: 'languageDetector',
  async: true,
  detect: async () => {
    const detected = await detectLanguageAndCountry();
    persistLocalization(detected);
    useLocalizationStore.setState(detected);
    if (rtlLanguages.includes(detected.language)) {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
    return detected.language;
  },
};

i18n.use(initReactI18next).use(languageDetector).use(Backend);
export { i18n, t };

export const initializeI18n = async ({ keyMode }: { keyMode?: boolean }) => {
  if (!i18n.isInitialized) {
    let devTranslations: Record<string, unknown> | null = null;
    if (import.meta.env.VITE_ALLOW_LOCAL_TRANSLATION_FALLBACK === 'true') {
      const module = await import('$/translations/de.json');
      devTranslations = module.default.translation;
    }

    await i18n.init<HttpBackendOptions>({
      debug: import.meta.env.VITE_NODE_ENV !== 'production',
      fallbackLng: 'en',
      backend: {
        loadPath: `${envVars.VITE_BASE_STORAGE_URL}/spectrum-translations/{{lng}}.json`,
        parse: (data) => {
          const parsedData = JSON.parse(data) as Record<string, unknown>;

          if (parsedData.translation != null) {
            if (devTranslations != null) {
              return deepMerge(
                devTranslations,
                parsedData.translation as Record<string, unknown>,
              );
            }
            return parsedData.translation;
          }

          return parsedData;
        },
      },
    });

    if (i18n.language === 'de') {
      await import('dayjs/locale/de');
    } else {
      await import('dayjs/locale/en');
    }
  }

  if (keyMode != null) {
    localStorage.setItem('translationKeyMode', keyMode.toString());
  }

  const isTranslationKeyMode =
    keyMode ?? localStorage.getItem('translationKeyMode') === 'true';

  if (isTranslationKeyMode) {
    await i18n.changeLanguage('cimode');
  }
};
